import Seo from "@/components/Seo";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api, DONATE_URL, GPM_URL } from "@/lib/api";
import { RevealLines, FadeUp, Stagger, StaggerItem } from "@/components/Reveal";

const GOALS = [
  "Elevate underrepresented voices",
  "Foster trust in local journalism",
  "Increase civic participation",
  "Support community-driven solutions",
  "Build a sustainable model for independent journalism",
  "Connect lived experience to policy",
];

export default function About() {
  const [correspondents, setCorrespondents] = useState([]);

  useEffect(() => {
    api.get("/correspondents").then(({ data }) => setCorrespondents(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="about-page">
      <Seo title="About" />
      <section className="pt-[120px] pb-16 max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
        <p className="label text-ink/50 mb-6">About</p>
        <RevealLines
          as="h1"
          className="font-display font-black text-[12vw] lg:text-[7vw] leading-[0.86] tracking-tight"
          lines={["Why the", "canary?"]}
        />
        <FadeUp delay={0.6}>
          <p className="mt-8 max-w-2xl text-xl text-ink/70 leading-relaxed">
            Miners once carried canaries underground. The bird&rsquo;s distress was the first warning
            of unseen danger. Black women are often the earliest to register the strain of our
            economic systems — and their experiences map where the whole society is headed.
          </p>
        </FadeUp>
      </section>

      {/* ADVOCACY JOURNALISM */}
      <section data-testid="advocacy-journalism-section" className="relative bg-ink text-white grain overflow-hidden">
        <div className="relative z-10 max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-4">
            <p className="label text-canary mb-4">Our Practice</p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight leading-[0.92] sticky top-28">
              Advocacy<br />Journalism
            </h2>
          </FadeUp>
          <div className="lg:col-span-7 lg:col-start-6 space-y-8 text-lg text-white/75 leading-relaxed">
            <FadeUp><p>We are transparent about our commitment: centering the communities most impacted by the systems we investigate.</p></FadeUp>
            <FadeUp delay={0.05}><p>We hold ourselves to rigorous standards of accuracy and fairness while openly advocating for justice, equity, and the dignity of the people whose stories we tell — because the most impacted are often the most impactful.</p></FadeUp>
          </div>
        </div>
      </section>

      <section className="bg-paper py-24 lg:py-32">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-4">
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight sticky top-28">Our mission</h2>
          </FadeUp>
          <div className="lg:col-span-7 lg:col-start-6 space-y-8 text-lg text-ink/75 leading-relaxed">
            <FadeUp><p>We produce community-centered journalism that documents how systems impact Black women, amplifies community expertise, and connects lived experience to larger social and economic trends.</p></FadeUp>
            <FadeUp delay={0.05}><p>We believe the people closest to a problem are closest to its solution. So we listen first — in living rooms, church basements, and community kitchens — before we report. We don&rsquo;t just tell the story, we evaluate the solutions available and help people access them.</p></FadeUp>
            <FadeUp delay={0.1}><p>Our signature initiative, <span className="text-ink font-semibold">Lift Every Voice</span>, convenes residents, journalists, nonprofit leaders, advocates, and public officials to turn dialogue into action across Colorado.</p></FadeUp>
          </div>
        </div>
      </section>

      {/* CORRESPONDENTS */}
      <section data-testid="correspondents-section" className="relative bg-ink text-white grain overflow-hidden">
        <div className="relative z-10 max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-24 lg:py-32">
          <FadeUp>
            <p className="label text-canary mb-4">Our Correspondents</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[0.95]">
              The people behind the reporting.
            </h2>
          </FadeUp>

          <Stagger className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {correspondents.map((c) => (
              <StaggerItem key={c.id}>
                <Link to={`/correspondent/${c.id}`} data-testid={`correspondent-card-${c.id}`} className="group block">
                  <div className="hover-img aspect-[4/5] bg-white/5 mb-5">
                    <img src={c.photo} alt={c.name} className="h-full w-full object-cover" />
                  </div>
                  <p className="label text-canary mb-2">{c.role}</p>
                  <h3 className="font-display text-2xl lg:text-3xl font-bold group-hover:text-canary transition-colors">{c.name}</h3>
                  <span className="inline-flex items-center gap-2 mt-3 label text-white/60 group-hover:text-white transition-colors">
                    View profile <ArrowUpRight size={14} />
                  </span>
                </Link>
              </StaggerItem>
            ))}
            {correspondents.length === 0 && (
              <p className="text-white/50">Correspondent profiles coming soon.</p>
            )}
          </Stagger>
        </div>
      </section>

      {/* LIFT EVERY VOICE EVENT */}
      <section data-testid="lift-event-section" className="bg-canary text-ink">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <FadeUp className="lg:col-span-7">
            <p className="label text-ink/60 mb-4">Signature Initiative · Upcoming Event</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.95]">
              Lift Every Voice
            </h2>
            <p className="mt-7 text-lg text-ink/75 leading-relaxed max-w-xl">
              A convening of residents, journalists, nonprofit leaders, advocates, and public
              officials — turning community expertise into solutions and action across Colorado.
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-4 lg:col-start-9">
            <div className="border-2 border-ink/25 divide-y divide-ink/25 bg-canary">
              <div className="p-6">
                <p className="label text-ink/50 mb-1">Date</p>
                <p className="font-display text-2xl font-bold">August 13, 2026</p>
              </div>
              <div className="p-6">
                <p className="label text-ink/50 mb-1">Time</p>
                <p className="font-display text-2xl font-bold">6:00 – 8:30 PM</p>
              </div>
              <div className="p-6">
                <p className="label text-ink/50 mb-1">Location</p>
                <p className="font-display text-2xl font-bold leading-tight">Cleo Parker Robinson Dance</p>
                <p className="text-ink/70 mt-1">Denver, Colorado</p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-24 lg:py-32">
        <FadeUp><p className="label text-ink/50 mb-4">Impact Goals</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[0.95]">What we are building toward.</h2>
        </FadeUp>
        <Stagger className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/15 border border-ink/15">
          {GOALS.map((g, i) => (
            <StaggerItem key={i}>
              <div className="bg-white p-8 h-full min-h-[200px] flex flex-col justify-between group hover:bg-canary transition-colors duration-300">
                <span className="font-display text-4xl font-black text-ink/20 group-hover:text-ink transition-colors">{String(i + 1).padStart(2, "0")}</span>
                <p className="font-display text-xl lg:text-2xl font-bold leading-tight mt-8">{g}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      <section className="bg-ink text-white grain relative overflow-hidden">
        <div className="relative z-10 max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-24 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-2xl leading-[1.02]">
            Stand with the communities we serve.
          </h2>
          <div className="flex flex-wrap gap-4">
            <a href={DONATE_URL} target="_blank" rel="noopener noreferrer" data-testid="about-donate-btn"
              className="inline-flex items-center gap-2 label bg-canary text-ink px-8 py-4 rounded-full hover:bg-white transition-colors duration-300">
              Donate <ArrowUpRight size={15} />
            </a>
            <Link to="/partner" data-testid="about-partner-btn"
              className="inline-flex items-center gap-2 label border border-white/40 px-8 py-4 rounded-full hover:bg-white hover:text-ink transition-colors duration-300">
              Partner with us
            </Link>
          </div>
        </div>
      </section>

      {/* GREATER PURPOSE MEDIA */}
      <section data-testid="gpm-section" className="bg-paper border-t border-ink/10">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-20 flex flex-col items-center text-center">
          <FadeUp>
            <p className="label text-ink/50 mb-6">A publication of</p>
            <a href={GPM_URL} target="_blank" rel="noopener noreferrer" data-testid="gpm-link"
              className="group inline-flex flex-col items-center gap-3">
              <span className="font-display text-3xl sm:text-4xl font-bold tracking-tight group-hover:text-ink/60 transition-colors">
                Greater Purpose Media
              </span>
              <span className="inline-flex items-center gap-2 label text-ink/60 group-hover:text-ink transition-colors">
                Visit greaterpurposemedia.com <ArrowUpRight size={15} />
              </span>
            </a>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
